import React, { FC, useCallback, useEffect, useState } from 'react';
import { AccountLocations } from './account/account-locations';
import { accountsService, feedbackAdminService, locationsService } from '@web/services/singletons';
import { Button, Icon, Label, LoadingCircle, Typography } from '@packages/ui/shared';
import { cloneDeep, omitBy } from 'lodash';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';
import { ToasterType } from '@web/components/toaster/toaster';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useContentOverlayState } from '@web/core/hooks/use-content-overlay';
import {
    Account,
    AccountLocation,
    AccountRoleOption,
    District,
    Region,
    ROLE_TARGET_ID,
    Zone,
} from '@packages/models/api';
import colors from '@packages/core/styles/colors';
import ReactDOM from 'react-dom';
import Select from '@web/components/select';
import { hasStisThirdPartyRole } from '@web/utils/can-view-stis';
import uuidv4 from 'uuid/v4';

const useStyles = createUseStyles({
    panel: {
        backgroundColor: colors.white,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 390,
        position: 'absolute',
        right: 0,
        top: 0,
        width: '100%',
        zIndex: 5,
    },
    disabledTypeahead: {
        opacity: 0.6,
        pointerEvents: 'none',
        cursor: 'not-allowed',
        '& .rbt': {
            opacity: 1,
        },
        '& .form-control': {
            backgroundColor: '#e9ecef',
            color: colors.grayFive,
        },
        '& .rbt-input-multi': {
            backgroundColor: '#e9ecef',
            borderColor: colors.grayFour,
        },
        '& input': {
            backgroundColor: '#e9ecef',
            color: colors.grayFive,
            cursor: 'not-allowed',
        },
        '& .rbt-input-main': {
            color: colors.grayFive,
        },
        '& .rbt-aux': {
            opacity: 0.5,
        },
        '& .dropdown-toggle': {
            backgroundColor: '#e9ecef',
            cursor: 'not-allowed',
        },
    },
    disabledField: {
        alignItems: 'center',
        backgroundColor: colors.grayThree,
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 3,
        color: colors.grayFive,
        display: 'flex',
        minHeight: 40,
        padding: '0 12px',
    },
    panelHeader: {
        alignItems: 'center',
        backgroundColor: colors.grayTwo,
        borderBottom: `1px solid ${colors.grayThree}`,
        display: 'flex',
        height: 56,
        justifyContent: 'space-between',
        padding: '0px 24px',
    },
    panelScrollContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflowY: 'auto',
        padding: 24,
    },
    actionButtons: { marginTop: 24 },
    closeButton: { '&:hover': { cursor: 'pointer' } },
    divider: { height: 1, backgroundColor: colors.grayThree },
    firstSection: { marginBottom: 24 },
    lastDivider: { marginTop: 'auto!important' },
    section: { marginBottom: 24, marginTop: 24 },
    panelFooter: {
        backgroundColor: colors.white,
        borderTop: `1px solid ${colors.grayThree}`,
        bottom: 0,
        padding: '16px 24px',
        position: 'sticky',
        zIndex: 2,
    },
    loadingOverlay: {
        alignItems: 'center',
        backgroundColor: 'rgba(255,255,255,0.7)',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        left: 0,
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 10,
    },
});

const styles = StyleSheet.create({
    label: { fontSize: 14, lineHeight: 16, display: 'flex', marginBottom: 8 },
    headerTitle: { fontSize: 15, fontWeight: '400', lineHeight: 24 },
});

const Divider: FC<{ className?: string }> = ({ className }) => <hr className={className} />;

export interface AccountLocationExtended extends AccountLocation {
    districtOptions: District[];
    districtSelection: string;
    isLoading: boolean;
    locationId: string;
    regionOptions: Region[];
    regionSelection: string;
    zoneOptions: Zone[];
    zoneSelection: string;
}

interface ManageUsersPanelProps {
    account?: Account;
    onAccountUpdate(account: Account): void;
    setShowToast: (toast: { show: boolean; message: string; type: ToasterType }) => void;
}

export const ManageUsersPanel: FC<ManageUsersPanelProps> = ({ account, onAccountUpdate, setShowToast }) => {
    const classes = useStyles();
    const { dismissAllOverlays } = useContentOverlayState();
    const isThirdPartyUser = hasStisThirdPartyRole(account);
    const [isLoading, setIsLoading] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [baseRoleOptions, setBaseRoleOptions] = useState<AccountRoleOption[]>([]);
    const [addOnRoleOptions, setAddOnRoleOptions] = useState<AccountRoleOption[]>([]);

    const [baseRoles, setBaseRoles] = useState<AccountRoleOption[]>(
        account?.baseRoles?.map((role) => ({
            description: role.roleDescription,
            roleId: role.roleId,
            accountRoleId: role.accountRoleId,
        })) ||
            [
                {
                    description: 'SELECT',
                    roleId: 'SELECT',
                    accountRoleId: 'SELECT',
                },
            ].map((role) => {
                return {
                    ...role,
                    roleId: uuidv4(),
                    isLoading: false,
                };
            })
    );

    const [addOnRoles, setAddOnRoles] = useState<AccountRoleOption[]>(account?.addOnRoles || []);
    const [locations, setLocations] = useState<AccountLocationExtended[]>(
        account?.locations.map((location) => {
            return {
                ...location,
                locationId: uuidv4(),
                isLoading: false,
                regionSelection: location.elements.find((e) => e.accountLocationElementType === 'REGION')?.value || '',
                zoneSelection: location.elements.find((e) => e.accountLocationElementType === 'ZONE')?.value || '',
                districtSelection:
                    location.elements.find((e) => e.accountLocationElementType === 'DISTRICT')?.value || '',
                zoneOptions: [],
                regionOptions: [],
                districtOptions: [],
            };
        }) || []
    );

    useEffect(() => {
        if (!account) {
            return;
        }

        setBaseRoles(
            account.baseRoles.map((role) => ({
                description: role.roleDescription,
                roleId: role.roleId,
                accountRoleId: role.accountRoleId,
            }))
        );
        setAddOnRoles(account.addOnRoles);
    }, [account]);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);

            try {
                const rolesResponse = await feedbackAdminService.fetchAccountRoles();

                if (rolesResponse.success) {
                    setBaseRoleOptions(rolesResponse.data.baseRoles);
                    setAddOnRoleOptions(rolesResponse.data.addOnRoles);
                } else {
                    throw rolesResponse.data;
                }

                const locationsClone = cloneDeep(locations);

                for (let i = 0; i < locationsClone.length; i++) {
                    const regionsResponse = await locationsService.getRegions();
                    if (regionsResponse.success) {
                        locationsClone[i].regionOptions = regionsResponse.data.regions;
                    } else {
                        throw regionsResponse.data;
                    }

                    if (locationsClone[i].zoneSelection) {
                        const districtResponse = await locationsService.getDistricts(locationsClone[i].zoneSelection);
                        if (districtResponse.success) {
                            locationsClone[i].districtOptions = districtResponse.data.districts;
                        } else {
                            throw districtResponse.data;
                        }
                    }

                    if (locationsClone[i].regionSelection) {
                        const zoneResponse = await locationsService.getZones(locationsClone[i].regionSelection);
                        if (zoneResponse.success) {
                            locationsClone[i].zoneOptions = zoneResponse.data.zones;
                        } else {
                            throw zoneResponse.data;
                        }
                    }
                }

                setLocations(locationsClone);
            } catch (error) {
                setShowToast({ show: true, message: error.message, type: 'error' });
            }

            setIsLoading(false);
        }

        fetchData();
    }, []);

    const handleSaveButtonClick = useCallback(async () => {
        setIsLoading(true);

        try {
            if (!account) {
                throw new Error('Account not found.');
            }

            const rawPayload = {
                addOnRoleIds: addOnRoles.map((addOnRole) => addOnRole.roleId),
                accountRoles: [{ ...baseRoles[0], accountRoleId: account.baseRoles[0].accountRoleId }],
                roleTargets: locations
                    .map((l) => {
                        if (l.roleTargetTypeId === ROLE_TARGET_ID.REGION) {
                            return {
                                roleTargetId: l.regionSelection,
                                roleId: l.roleId,
                            };
                        } else if (l.roleTargetTypeId === ROLE_TARGET_ID.ZONE) {
                            return {
                                roleTargetId: l.zoneSelection,
                                roleId: l.roleId,
                            };
                        } else if (l.roleTargetTypeId === ROLE_TARGET_ID.DISTRICT) {
                            return {
                                roleTargetId: l.districtSelection,
                                roleId: l.roleId,
                            };
                        }

                        return undefined;
                    })
                    .filter((rtti) => {
                        return rtti !== undefined && Boolean(rtti.roleTargetId) && Boolean(rtti.roleId);
                    }),
            };
            const payload = omitBy(rawPayload, (value) => {
                return !Array.isArray(value) && !value;
            });

            const response = await accountsService.updateAccount(account.accountId, payload);

            if (response.success) {
                onAccountUpdate(response.data.account);
                setShowToast({ show: true, message: 'Account updated successfully', type: 'success' });
                dismissAllOverlays();
            } else {
                throw response.data;
            }
        } catch (error) {
            setShowToast({ show: true, message: error.message, type: 'error' });
        }

        setIsLoading(false);
    }, [account, addOnRoles, baseRoles, locations, onAccountUpdate]);

    const handleRegionSelectChange = useCallback(
        async (locationId: any, regionId: any) => {
            const locationsClone = cloneDeep(locations);
            const locationIndex = locations.findIndex((l) => l.locationId === locationId);

            try {
                const response = await locationsService.getZones(regionId);

                if (response.success) {
                    locationsClone[locationIndex].regionSelection = regionId;
                    locationsClone[locationIndex].zoneOptions = response.data.zones;

                    setLocations(locationsClone);
                } else {
                    throw response.data;
                }
            } catch (error) {
                setShowToast({ show: true, message: error.message, type: 'error' });
            }
        },
        [locations]
    );

    const handleZoneSelectChange = useCallback(
        async (locationId: any, zoneId: any) => {
            const locationsClone = cloneDeep(locations);
            const locationIndex = locations.findIndex((l) => l.locationId === locationId);

            try {
                const response = await locationsService.getDistricts(zoneId);

                if (response.success) {
                    locationsClone[locationIndex].zoneSelection = zoneId;
                    locationsClone[locationIndex].districtOptions = response.data.districts;

                    setLocations(locationsClone);
                } else {
                    throw response.data;
                }
            } catch (error) {
                setShowToast({ show: true, message: error.message, type: 'error' });
            }
        },
        [locations]
    );

    const handleDistrictSelectChange = useCallback(
        (locationId: any, districtId: any) => {
            const locationsClone = cloneDeep(locations);
            const locationIndex = locations.findIndex((l) => l.locationId === locationId);

            locationsClone[locationIndex].districtSelection = districtId;

            setLocations(locationsClone);
        },
        [locations]
    );

    const checkIfChanged = useCallback(() => {
        if (!account) return false;

        const baseRolesChanged =
            baseRoles.length !== (account.baseRoles?.length || 0) ||
            baseRoles.some((role, idx) => account.baseRoles?.[idx]?.roleId !== role.roleId);

        const addOnRolesChanged =
            addOnRoles.length !== (account.addOnRoles?.length || 0) ||
            addOnRoles.some((role, idx) => account.addOnRoles?.[idx]?.roleId !== role.roleId);

        // Locations changed: compare each location's region/zone/district selection vs initial
        const locationsChanged = locations.some((loc, idx) => {
            const initialLoc = account.locations?.[idx];
            if (!initialLoc) return true;

            const initialRegion =
                initialLoc.elements.find((e) => e.accountLocationElementType === 'REGION')?.value || '';
            const initialZone = initialLoc.elements.find((e) => e.accountLocationElementType === 'ZONE')?.value || '';
            const initialDistrict =
                initialLoc.elements.find((e) => e.accountLocationElementType === 'DISTRICT')?.value || '';

            return (
                loc.regionSelection !== initialRegion ||
                loc.zoneSelection !== initialZone ||
                loc.districtSelection !== initialDistrict
            );
        });

        return baseRolesChanged || addOnRolesChanged || locationsChanged;
    }, [account, baseRoles, addOnRoles, locations]);

    useEffect(() => {
        setIsEnabled(checkIfChanged());
    }, [checkIfChanged]);

    const rightPanelDiv = document.getElementById('right-side-panel');
    if (!rightPanelDiv) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={classes.panel}>
            {isLoading && (
                <div className={classes.loadingOverlay}>
                    <LoadingCircle size={64} borderWidth={8} borderColor={colors.grayOne} />
                </div>
            )}

            <div className={classes.panelHeader}>
                <Typography style={styles.headerTitle}>View Details</Typography>
                <div className={classes.closeButton} onClick={dismissAllOverlays}>
                    <Icon name="x-close" />
                </div>
            </div>

            <div className={classes.panelScrollContainer}>
                <Divider className={classes.divider} />
                <div className={classes.firstSection}>
                    <Typography variant="caption2" color="graySix">
                        Subaru Username
                    </Typography>
                    <Typography style={styles.label}>{account?.soaUsername || 'N/A'}</Typography>

                    <Typography variant="caption2" color="graySix">
                        Full Name
                    </Typography>
                    <Typography style={styles.label}>{account?.name}</Typography>

                    <Typography variant="caption2" color="graySix">
                        Phone
                    </Typography>
                    <Typography style={styles.label}>
                        {account?.phoneNumbers?.length ? (
                            account.phoneNumbers.map((phoneNumber) => (
                                <Typography key={phoneNumber.phoneNumberId}>{phoneNumber.phoneNumber}</Typography>
                            ))
                        ) : (
                            <Typography>N/A</Typography>
                        )}
                    </Typography>

                    <Typography variant="caption2" color="graySix">
                        Email
                    </Typography>
                    <Typography style={styles.label}>
                        {account?.emailAddresses?.length
                            ? account.emailAddresses.map((emailAddress) => emailAddress.emailAddress).join(', ')
                            : 'N/A'}
                    </Typography>
                </div>

                <Divider className={classes.divider} />
                {isThirdPartyUser && (
                    <div
                        style={{
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: 4,
                            padding: '8px 12px',
                            marginTop: 16,
                        }}
                    >
                        <Typography variant="caption2" color="graySix">
                            Third-party user roles cannot be modified
                        </Typography>
                    </div>
                )}
                <div className={classes.section}>
                    <Label style={styles.label} required>
                        Role
                    </Label>
                    <Select
                        value={baseRoles[0]?.roleId || 'SELECT'}
                        options={[
                            { value: 'SELECT', title: 'SELECT' },
                            ...baseRoleOptions.map((option) => ({
                                value: option.roleId,
                                title: option.description,
                            })),
                        ]}
                        onChange={(event) => {
                            const selectedRole = baseRoleOptions.filter(
                                (option) => option.roleId === event.currentTarget.value
                            );
                            setBaseRoles(selectedRole);
                        }}
                        required
                        disabled={isThirdPartyUser}
                    />
                </div>

                <Divider className={classes.divider} />
                <div className={classes.section}>
                    <Label style={styles.label}>Add On Role</Label>
                    {isThirdPartyUser ? (
                        <div className={classes.disabledField}>
                            {addOnRoles.length > 0
                                ? addOnRoles.map((role) => role.description).join(', ')
                                : 'Search for roles...'}
                        </div>
                    ) : (
                        <Typeahead
                            id="manage-users-async-typeahead"
                            placeholder="Search for roles..."
                            labelKey="description"
                            multiple
                            selected={addOnRoles}
                            onChange={setAddOnRoles}
                            options={addOnRoleOptions.filter(
                                (option) => !addOnRoles.some((role) => role.roleId === option.roleId)
                            )}
                        />
                    )}
                </div>

                <Divider className={`${classes.divider} ${classes.lastDivider}`} />

                <AccountLocations
                    locations={locations}
                    account={account}
                    handleDistrictSelectChange={handleDistrictSelectChange}
                    handleRegionSelectChange={handleRegionSelectChange}
                    handleZoneSelectChange={handleZoneSelectChange}
                    disabled={isThirdPartyUser}
                />

                <Divider className={`${classes.divider} ${classes.lastDivider}`} />
            </div>

            <div className={classes.panelFooter}>
                <div className={`d-flex flex-row align-items-center justify-content-between ${classes.actionButtons}`}>
                    <Button onPress={handleSaveButtonClick} disabled={!isEnabled || isThirdPartyUser}>
                        Save Changes
                    </Button>
                    <Button variant="ghost-blue" onPress={dismissAllOverlays}>
                        Cancel
                    </Button>
                </div>
            </div>
        </div>,
        rightPanelDiv
    );
};
